/**
 * 红色模版独立页脚
 * 
 * 设计理念：紧凑实用，分类信息网站风格
 * - 三列布局：品牌介绍 / 快捷链接 / 联系我们
 * - 简洁底栏：版权 + ICP
 */

import { Link } from "react-router-dom";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function RedFooter() {
  const { siteConfig } = useSiteConfig();

  const phone = siteConfig?.contact_info?.phone || '';
  const email = siteConfig?.contact_info?.email || '';
  const address = siteConfig?.contact_info?.address;
  const companyName = siteConfig?.contact_info?.company_name;
  const icp = siteConfig?.icp || '';
  const icpUrl = siteConfig?.icp_url || '';
  const displayName = siteConfig?.site_name || '';
  const siteName = siteConfig?.name || '';
  const logo = siteConfig?.logo || '';
  const footerInfo = siteConfig?.footer_info || '';

  const defaultMenus = [
    { id: "m1", label: "首页", href: "/", external: false, enabled: true },
    { id: "m2", label: "发布信息", href: "/publish", external: false, enabled: true },
    { id: "m3", label: "开通VIP", href: "/vip", external: false, enabled: true },
    { id: "m4", label: "会员中心", href: "/dashboard", external: false, enabled: true },
    { id: "m5", label: "帮助中心", href: "/page/help", external: false, enabled: true },
    { id: "m6", label: "关于我们", href: "/page/about", external: false, enabled: true },
  ];

  const enableVip = siteConfig?.enable_vip ?? true;
  const hasFooterMenus = siteConfig?.footer_menus && Array.isArray(siteConfig.footer_menus) && siteConfig.footer_menus.length > 0;
  const menus = (hasFooterMenus
    ? siteConfig.footer_menus.filter((m: any) => m.enabled !== false)
    : defaultMenus)
    .filter((m: any) => m.href !== '/vip' || enableVip);

  const currentYear = new Date().getFullYear();
  const copyrightOwner = companyName || displayName || siteName;
  const copyright = copyrightOwner ? `© ${currentYear} ${copyrightOwner} 版权所有` : `© ${currentYear} 版权所有`;

  const brandName = displayName || siteName;
  const quickLinks = menus.slice(0, 6);

  return (
    <footer className="bg-gray-900 text-gray-300 border-t-4 border-red-600">
      {/* 主体区域 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">

          {/* 品牌介绍 */}
          <div>
            <div className="flex items-center gap-2.5 mb-3">
              {logo ? (
                <img src={logo} alt={brandName} className="h-7 w-7 object-contain rounded" />
              ) : (
                <div className="h-7 w-7 rounded-md bg-red-600 flex items-center justify-center text-white text-xs font-bold">
                  {brandName ? brandName.charAt(0) : 'S'}
                </div>
              )}
              {brandName && (
                <span className="text-base font-bold text-white">{brandName}</span>
              )}
            </div>
            {footerInfo ? (
              <p className="text-sm text-gray-400 leading-relaxed">{footerInfo}</p>
            ) : (
              <p className="text-sm text-gray-400 leading-relaxed">
                本地分类信息平台，汇聚各类生活服务信息，发布查找都方便。
              </p>
            )}
          </div>

          {/* 快捷链接 */}
          <div>
            <h4 className="text-xs font-bold text-red-400 uppercase tracking-wider mb-4">快捷链接</h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {quickLinks.map((item: any) => (
                <div key={item.id}>
                  {item.external ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="text-sm text-gray-400 hover:text-red-400 transition-colors"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      to={item.href}
                      className="text-sm text-gray-400 hover:text-red-400 transition-colors"
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 联系我们 */}
          <div>
            <h4 className="text-xs font-bold text-red-400 uppercase tracking-wider mb-4">联系我们</h4>
            <ul className="space-y-3">
              {phone && (
                <li>
                  <a
                    href={`tel:${phone}`}
                    className="flex items-center gap-2.5 text-sm text-gray-400 hover:text-red-400 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center shrink-0">
                      <i className="ri-phone-line text-red-400" />
                    </div>
                    {phone}
                  </a>
                </li>
              )}
              {email && (
                <li>
                  <a
                    href={`mailto:${email}`}
                    className="flex items-center gap-2.5 text-sm text-gray-400 hover:text-red-400 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center shrink-0">
                      <i className="ri-mail-line text-red-400" />
                    </div>
                    {email}
                  </a>
                </li>
              )}
              {address && (
                <li>
                  <div className="flex items-center gap-2.5 text-sm text-gray-400">
                    <div className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center shrink-0">
                      <i className="ri-map-pin-line text-red-400" />
                    </div>
                    {address}
                  </div>
                </li>
              )}
              {!phone && !email && !address && (
                <li className="text-sm text-gray-500">暂无联系方式</li>
              )}
            </ul>
          </div>

        </div>
      </div>

      {/* 底栏 */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <span className="text-xs text-gray-500">{copyright}</span>
          {icp && (
            <a
              href={icpUrl}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="text-xs text-gray-500 hover:text-red-400 transition-colors"
            >
              {icp}
            </a>
          )}
        </div>
      </div>
    </footer>
  );
}
